import { Status } from './vulnerability-status'
import { BaseAssessRule } from './assess-rules'
import { serverEnvI18n, ServerEnvironment } from './server-environment'
import { VulnerabilitySeverity } from '../graphql/schema/shared'

export type ContrastProduct = 'PROTECT' | 'ASSESS'
export type ConfidentialityImpact = 'None' | 'Partial' | 'Complete'
export type IntegrityImpact = 'None' | 'Partial' | 'Complete'
export type AvailabilityImpact = 'None' | 'Partial' | 'Complete'
export type AuthenticationRequired = 'None' | 'Single' | 'Multiple'
export type AccessVector = 'Local' | 'Adjacent Network' | 'Network'
export type AccessComplexity = 'Low' | 'Medium' | 'High'
interface VulnerabilityReference {
  readonly href: string
}
export interface Vulnerability {
  name: string
  description: string
  authentication?: AuthenticationRequired
  references?: VulnerabilityReference[]
  visible?: boolean
  accessVector?: AccessVector
  accessComplexity?: AccessComplexity
  availabilityImpact?: AvailabilityImpact
  confidentialityImpact?: ConfidentialityImpact
  integrityImpact?: IntegrityImpact
  severityValue?: number
  severityCode?: VulnerabilitySeverity
  hasCve: boolean
  cvss3Vector?: string
  cvss3SeverityValue?: number
  cvss3SeverityCode?: VulnerabilitySeverity
  hasCvss3?: boolean
  // COSMO properties
  epssScore?: number
  epssPercentile?: number
  cisa?: boolean
  cvssScoreSource?: string
}

export type RuleSeverity = 'NOTE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
export type VulnerabilityStatus =
  | 'Reported'
  | 'Suspicious'
  | 'Confirmed'
  | 'NotAProblem'
  | 'Remediated'
  | 'Fixed'
  | 'AutoRemediated'

export type VulnerabilityType = 'severity' | 'status' | 'rule' | 'tags'
export type ServerType = 'environment' | 'tags'

export interface TypedVulnerabilities {
  readonly id: string | RuleSeverity | VulnerabilityStatus
  readonly name: string
  readonly type: VulnerabilityType
}

export interface TypedServers {
  readonly id: string | ServerEnvironment
  readonly name: string
  readonly type: ServerType
}

export interface RuleSeverityItem {
  readonly value: RuleSeverity
  readonly name: string
}

export const allRuleSeverities: RuleSeverityItem[] = [
  {
    value: 'NOTE',
    name: 'title_vulnRuleSeverityTypeNote'
  },
  {
    value: 'LOW',
    name: 'title_vulnRuleSeverityTypeLow'
  },
  {
    value: 'MEDIUM',
    name: 'title_vulnRuleSeverityTypeMedium'
  },
  {
    value: 'HIGH',
    name: 'title_vulnRuleSeverityTypeHigh'
  },
  {
    value: 'CRITICAL',
    name: 'title_vulnRuleSeverityTypeCritical'
  }
]

export function normalizeRuleSeveritiesToTypedVulnerabilities(
  ruleSeverityItems: RuleSeverityItem[],
  intl: any
): TypedVulnerabilities[] {
  return ruleSeverityItems.map(({ name, value }) => ({
    name: intl.formatMessage({ id: name }),
    id: value,
    type: 'severity'
  }))
}

export function normalizeAssessRulesToTypedVulnerabilities(
  rules: BaseAssessRule[]
): TypedVulnerabilities[] {
  return rules.map(({ name, title }) => ({
    name: title,
    id: name,
    type: 'rule'
  }))
}

export function normalizeStatusesToTypedVulnerabilities(
  statuses: Status[],
  intl: any
): TypedVulnerabilities[] {
  return statuses.map(({ name, i18nID }) => ({
    name: intl.formatMessage({ id: i18nID }),
    id: name,
    type: 'status'
  }))
}

export function normalizeTagsToTypedVulnerabilities(
  tags: string[]
): TypedVulnerabilities[] {
  return tags.map((tag) => ({
    name: tag,
    id: tag,
    type: 'tags'
  }))
}

export function normalizeEnvironmentsToTypedServers(
  environments: ServerEnvironment[],
  intl: any
): TypedServers[] {
  return environments.map((env) => ({
    name: intl.formatMessage({ id: serverEnvI18n[env] }),
    id: env,
    type: 'environment'
  }))
}

export function normalizeTagsToTypedServers(tags: string[]): TypedServers[] {
  return tags.map((tag) => ({
    name: tag,
    id: tag,
    type: 'tags'
  }))
}

export type Locale = 'en' | 'es'
